/**
 * HumanBubble — balão de mensagem humana estilo WhatsApp.
 *
 * - Mensagem própria (isOwn): alinhada à DIREITA, fundo esverdeado (teal-1),
 *   sem avatar/nome visível, exibe tiques ✓ (enviado) / ✓✓ azul (lido).
 * - Mensagem alheia: alinhada à ESQUERDA, avatar + nome do autor.
 * - Corpo com @menções destacadas (sem dangerouslySetInnerHTML).
 * - Citação (QuotedPreview) quando metadata.reply_to existe.
 * - Swipe horizontal (mobile) e botão de responder (desktop) via onReply.
 * - Mensagem apagada: tombstone "(Mensagem apagada)" — sem corpo, sem menu.
 * - Mensagem editada: badge "(editado)" ao lado da hora.
 * - Menu de ações (Editar / Apagar) para mensagens próprias não apagadas.
 */
import { useRef, useState } from 'react'
import {
  Avatar,
  Box,
  Button,
  Group,
  Modal,
  Paper,
  Text,
  Textarea,
  UnstyledButton,
} from '@mantine/core'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import {
  IconCheck,
  IconChecks,
  IconEdit,
  IconEyeOff,
  IconInfoCircle,
  IconTrash,
} from '@tabler/icons-react'
import type { ChatMessage } from '@/api/chat'
import { AttachmentCard } from './AttachmentCard'
import { QuotedPreview } from './QuotedPreview'
import { ReactionPills } from './ReactionPills'
import { BubbleActions } from './BubbleActions'
import { MessageInfoDrawer } from './MessageInfoDrawer'
import { renderRichText } from '../richText'
import { useAuthStore } from '@/store/auth'
import { useEditMessage, useDeleteMessage, useHideMessage, useSetReaction, useRemoveReaction } from '@/hooks/useChat'

function formatTime(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

/** Retorna true se a mensagem ainda está dentro da janela de 15 min para edição. */
function withinEditWindow(iso: string): boolean {
  return Date.now() - new Date(iso).getTime() <= 15 * 60 * 1000
}


// Limiar de deslocamento horizontal para disparar o swipe-to-reply (px)
const SWIPE_THRESHOLD = 60

interface Props {
  cid: number
  message: ChatMessage
  onReply?: (message: ChatMessage) => void
  /** Oculta avatar+nome por mensagem (conversa 1:1, estilo WhatsApp — a
   *  identidade do par já aparece no cabeçalho da conversa). */
  hideIdentity?: boolean
}

export function HumanBubble({ cid, message, onReply, hideIdentity }: Props) {
  const myId = useAuthStore((s) => s.user)?.id
  const isOwn = message.author_user_id != null && message.author_user_id === myId

  const displayName = message.author_name ?? `#${message.author_user_id ?? '?'}`
  const initials = message.author_name ? getInitials(message.author_name) : '?'
  const isMobile = useMediaQuery('(max-width: 768px)')

  // Modais de editar / apagar / apagar para mim
  const [editOpened, { open: openEdit, close: closeEdit }] = useDisclosure(false)
  const [deleteOpened, { open: openDelete, close: closeDelete }] = useDisclosure(false)
  const [hideOpened, { open: openHide, close: closeHide }] = useDisclosure(false)
  const [infoOpened, { open: openInfo, close: closeInfo }] = useDisclosure(false)
  const [editText, setEditText] = useState('')

  const editMutation = useEditMessage(cid)
  const deleteMutation = useDeleteMessage(cid)
  const hideMutation = useHideMessage(cid)
  const setReaction = useSetReaction(cid)
  const removeReaction = useRemoveReaction(cid)

  function onToggleReaction(emoji: string) {
    if (message.my_reaction === emoji) removeReaction.mutate({ mid: message.id })
    else setReaction.mutate({ mid: message.id, emoji })
  }

  function handleOpenEdit() {
    setEditText(message.body ?? '')
    openEdit()
  }

  function handleConfirmEdit() {
    const trimmed = editText.trim()
    if (!trimmed) return
    editMutation.mutate(
      { mid: message.id, text: trimmed },
      { onSuccess: closeEdit },
    )
  }

  function handleConfirmDelete() {
    deleteMutation.mutate(message.id, { onSuccess: closeDelete })
  }

  function handleConfirmHide() {
    hideMutation.mutate(message.id, { onSuccess: closeHide })
  }

  // Swipe state
  const touchStartX = useRef<number | null>(null)
  const touchStartY = useRef<number | null>(null)
  const [swipeDeltaX, setSwipeDeltaX] = useState(0)
  const swipeTriggered = useRef(false)

  function handleTouchStart(e: React.TouchEvent) {
    if (!onReply) return
    touchStartX.current = e.touches[0].clientX
    touchStartY.current = e.touches[0].clientY
    swipeTriggered.current = false
    setSwipeDeltaX(0)
  }

  function handleTouchMove(e: React.TouchEvent) {
    if (!onReply || touchStartX.current === null || touchStartY.current === null) return
    const dx = e.touches[0].clientX - touchStartX.current
    const dy = e.touches[0].clientY - touchStartY.current

    // Só processa se o movimento for predominantemente horizontal
    if (Math.abs(dy) > Math.abs(dx)) return

    if (dx > 0) {
      setSwipeDeltaX(Math.min(dx, SWIPE_THRESHOLD + 20))
    }
  }

  function handleTouchEnd() {
    if (!onReply) return
    if (swipeDeltaX >= SWIPE_THRESHOLD && !swipeTriggered.current) {
      swipeTriggered.current = true
      onReply(message)
    }
    setSwipeDeltaX(0)
    touchStartX.current = null
    touchStartY.current = null
  }

  const replyTo = message.metadata?.reply_to

  // Tique de leitura (apenas para mensagens próprias com status conhecido)
  const readTick =
    isOwn && typeof message.read === 'boolean' ? (
      message.read ? (
        <IconChecks size={14} color="var(--mantine-color-blue-5)" />
      ) : (
        <IconCheck size={14} color="var(--mantine-color-gray-5)" />
      )
    ) : null

  // Condições do menu
  const canEdit =
    isOwn &&
    message.kind === 'human' &&
    !message.deleted &&
    withinEditWindow(message.created_at)
  const canDelete = isOwn && message.kind === 'human' && !message.deleted
  const canHideForMe = !message.deleted
  // "Dados da mensagem" — só no balão próprio (mensagem humana, não apagada).
  const canViewInfo = isOwn && message.kind === 'human' && !message.deleted

  // Itens "Mais" do menu do BubbleActions (dados/editar/ocultar/apagar) — abrem os modais/drawers existentes.
  const extraItems =
    !message.deleted && (canViewInfo || canEdit || canHideForMe || canDelete) ? (
      <>
        {canViewInfo && (
          <ExtraActionRow
            icon={<IconInfoCircle size={14} />}
            label="Dados da mensagem"
            onClick={openInfo}
          />
        )}
        {canEdit && (
          <ExtraActionRow
            icon={<IconEdit size={14} />}
            label="Editar"
            onClick={handleOpenEdit}
          />
        )}
        {canHideForMe && (
          <ExtraActionRow
            icon={<IconEyeOff size={14} color="var(--mantine-color-orange-6)" />}
            label="Apagar para mim"
            onClick={openHide}
          />
        )}
        {canDelete && (
          <ExtraActionRow
            icon={<IconTrash size={14} color="var(--mantine-color-red-6)" />}
            label="Apagar"
            onClick={openDelete}
          />
        )}
      </>
    ) : undefined

  return (
    <>
      {/* Modal de edição */}
      <Modal
        opened={editOpened}
        onClose={closeEdit}
        title="Editar mensagem"
        centered
        size="sm"
      >
        <Textarea
          value={editText}
          onChange={(e) => setEditText(e.currentTarget.value)}
          minRows={3}
          autosize
          maxRows={8}
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
              e.preventDefault()
              handleConfirmEdit()
            }
          }}
        />
        <Group justify="flex-end" mt="md" gap="sm">
          <Button variant="subtle" color="gray" onClick={closeEdit}>
            Cancelar
          </Button>
          <Button
            color="teal"
            onClick={handleConfirmEdit}
            loading={editMutation.isPending}
            disabled={!editText.trim()}
          >
            Salvar
          </Button>
        </Group>
      </Modal>

      {/* Modal de confirmação de exclusão */}
      <Modal
        opened={deleteOpened}
        onClose={closeDelete}
        title="Apagar mensagem"
        centered
        size="sm"
      >
        <Text size="sm">
          Tem certeza que deseja apagar esta mensagem? Esta ação não pode ser desfeita.
        </Text>
        <Group justify="flex-end" mt="md" gap="sm">
          <Button variant="subtle" color="gray" onClick={closeDelete}>
            Cancelar
          </Button>
          <Button
            color="red"
            onClick={handleConfirmDelete}
            loading={deleteMutation.isPending}
          >
            Apagar
          </Button>
        </Group>
      </Modal>

      {/* Modal de confirmação de apagar para mim */}
      <Modal
        opened={hideOpened}
        onClose={closeHide}
        title="Apagar para mim"
        centered
        size="sm"
      >
        <Text size="sm">
          Esta mensagem será removida apenas da sua visualização. Os outros participantes continuarão vendo-a.
        </Text>
        <Group justify="flex-end" mt="md" gap="sm">
          <Button variant="subtle" color="gray" onClick={closeHide}>
            Cancelar
          </Button>
          <Button
            color="orange"
            onClick={handleConfirmHide}
            loading={hideMutation.isPending}
          >
            Apagar para mim
          </Button>
        </Group>
      </Modal>

      {/* Drawer "Dados da mensagem" (padrão WhatsApp) — só no balão próprio */}
      {canViewInfo && (
        <MessageInfoDrawer
          opened={infoOpened}
          onClose={closeInfo}
          cid={cid}
          message={message}
        />
      )}

      <Box
        id={`msg-${message.id}`}
        style={{
          maxWidth: isMobile ? '90%' : 'min(75%, 600px)',
          // WhatsApp: o balão ABRAÇA o conteúdo (não é um bloco de largura fixa).
          // Antes width:'100%' fazia o balão ocupar toda a faixa e a mensagem
          // enviada ficava "solta" longe da margem direita.
          width: 'fit-content',
          position: 'relative',
          // Próprio → direita; recebido → esquerda.
          marginLeft: isOwn ? 'auto' : undefined,
          marginRight: isOwn ? 0 : 'auto',
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <BubbleActions
          isMobile={!!isMobile}
          onReact={onToggleReaction}
          onReply={onReply ? () => onReply(message) : undefined}
          onCopy={() => navigator.clipboard.writeText(message.body ?? '')}
          extraItems={extraItems}
        >
        <Group
          align="flex-start"
          gap="xs"
          wrap="nowrap"
          justify={isOwn ? 'flex-end' : 'flex-start'}
        >
          {/* Avatar — só em mensagens alheias E fora de conversa 1:1 (WhatsApp:
              na DM a foto do par fica no cabeçalho, não em cada balão). */}
          {!isOwn && !hideIdentity && (
            <Avatar
              size="sm"
              radius="xl"
              color="teal"
              variant="filled"
              src={message.author_avatar_url ?? undefined}
              style={{ flexShrink: 0, marginTop: 2 }}
            >
              {initials}
            </Avatar>
          )}

          <Paper
            px="md"
            py="sm"
            withBorder
            style={{
              // Balão abraça o conteúdo em ambos os lados (não estica na faixa).
              flex: undefined,
              minWidth: 0,
              maxWidth: '100%',
              // Próprio: balão enviado theme-aware (estilo WhatsApp), borda direita.
              // Alheio: fundo --ai-bg-subtle, borda esquerda (comportamento original).
              borderLeft: isOwn ? undefined : '3px solid var(--mantine-color-teal-4)',
              borderRight: isOwn ? '3px solid var(--mantine-color-teal-5)' : undefined,
              backgroundColor: isOwn
                ? 'var(--ai-chat-sent-bg)'
                : 'var(--ai-bg-subtle)',
              // Cor de texto explícita no balão enviado: garante contraste no dark
              // (antes o texto herdava --ai-text-primary claro sobre fundo claro).
              color: isOwn ? 'var(--ai-chat-sent-text)' : undefined,
              transform: swipeDeltaX > 0 ? `translateX(${swipeDeltaX}px)` : undefined,
              transition: swipeDeltaX === 0 ? 'transform 0.2s ease' : undefined,
            }}
          >
            {/* Citação — exibida no topo quando há reply_to (nunca em mensagem apagada) */}
            {!message.deleted && replyTo && <QuotedPreview replyTo={replyTo} />}

            {/* Cabeçalho: só o NOME do autor (grupos; oculto em DM/próprio).
                A hora + (editado) + tique foram para o rodapé do balão (estilo
                WhatsApp) — ver bloco de meta abaixo. */}
            {!isOwn && !hideIdentity && (
              <Text size="xs" fw={600} c="teal.8" mb={2} lineClamp={1} style={{ textAlign: 'left' }}>
                {displayName}
              </Text>
            )}

            {/* Corpo: tombstone se apagada, caso contrário corpo normal com menções */}
            {message.deleted ? (
              <Text size="sm" fs="italic" c="dimmed">
                🚫 Mensagem apagada
              </Text>
            ) : (
              <Text size="sm" style={{ whiteSpace: 'pre-wrap', textAlign: 'left', overflowWrap: 'anywhere', color: isOwn ? 'var(--ai-chat-sent-text)' : undefined }}>
                {renderRichText(message.body ?? '')}
              </Text>
            )}

            {/* Anexos: imagens inline, outros arquivos como card */}
            {!message.deleted && message.attachments?.map((att) =>
              att.mime_type.startsWith('image/') && att.url ? (
                <img
                  key={att.id}
                  src={att.url}
                  alt={att.filename}
                  style={{ maxWidth: 260, maxHeight: 260, borderRadius: 8, cursor: 'pointer', display: 'block', marginTop: 6 }}
                  onClick={() => window.open(att.url as string, '_blank', 'noopener')}
                />
              ) : (
                <AttachmentCard key={att.id} att={att} />
              )
            )}

            {/* Rodapé (estilo WhatsApp): hora + (editado) + tique, no canto
                inferior direito do balão. */}
            <Group gap={4} wrap="nowrap" mt={2} align="center" justify="flex-end">
              <Text
                size="xs"
                c={isOwn ? undefined : 'dimmed'}
                style={{ flexShrink: 0, fontSize: 11, lineHeight: 1, color: isOwn ? 'var(--ai-chat-sent-text)' : undefined, opacity: isOwn ? 0.7 : 0.8 }}
              >
                {formatTime(message.created_at)}
              </Text>
              {message.edited && !message.deleted && (
                <Text
                  size="xs"
                  c={isOwn ? undefined : 'dimmed'}
                  style={{ flexShrink: 0, fontSize: 11, lineHeight: 1, color: isOwn ? 'var(--ai-chat-sent-text)' : undefined, opacity: isOwn ? 0.7 : 0.8 }}
                >
                  (editado)
                </Text>
              )}
              {readTick && (
                <Box style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                  {readTick}
                </Box>
              )}
            </Group>

            {/* Pills de reações */}
            {!message.deleted && (
              <ReactionPills
                reactions={message.reactions ?? []}
                myReaction={message.my_reaction}
                onToggle={onToggleReaction}
              />
            )}
          </Paper>
        </Group>
        </BubbleActions>
      </Box>
    </>
  )
}

/** Linha de ação "Mais" do menu do BubbleActions (editar/ocultar/apagar). */
function ExtraActionRow({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode
  label: string
  onClick: () => void
}) {
  return (
    <UnstyledButton
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        padding: '6px 10px',
        borderRadius: 6,
        fontSize: 14,
        color: 'var(--mantine-color-text)',
      }}
      onMouseEnter={(ev) => {
        ;(ev.currentTarget as HTMLButtonElement).style.background =
          'var(--mantine-color-default-hover)'
      }}
      onMouseLeave={(ev) => {
        ;(ev.currentTarget as HTMLButtonElement).style.background = 'transparent'
      }}
    >
      <Group gap={8} wrap="nowrap">
        {icon}
        <Text size="sm">{label}</Text>
      </Group>
    </UnstyledButton>
  )
}
